import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {ConnectionOperation} from '@github-ui/optimistic-connection'
import {
  createConnectionMutationId,
  dispatchConnectionMutation,
} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {commitMutation, graphql} from 'react-relay'
import type {Disposable, Environment, RecordSourceProxy, RecordSourceSelectorProxy} from 'relay-runtime'
import type {
  AddSubIssueInput,
  addSubIssueMutation,
  addSubIssueMutation$data,
} from './__generated__/addSubIssueMutation.graphql'
import {md2html} from '@github-ui/md2html'
import {
  buildSubIssueOptimisticNode,
  toRowState,
  toRowStateReason,
  type SubIssueOptimisticNodeInput,
} from './sub-issue-optimistic-node'
import type {SubIssuesListItemNode} from '../components/sub-issues-list-item-query-types'

const SUB_ISSUES_CONNECTION_PATH = ['subIssues']

function getSubIssuesConnection(parentRecord: NonNullable<ReturnType<RecordSourceProxy['get']>>) {
  return parentRecord.getLinkedRecord('subIssues', {first: 100}) || null
}

function getSubIssuesConnectionAlias(parentRecord: NonNullable<ReturnType<RecordSourceProxy['get']>>) {
  return parentRecord.getLinkedRecord('subIssuesConnection') || null
}

function ensureSubIssueRequiredFields(
  store: RecordSourceProxy,
  subIssueRecord: NonNullable<ReturnType<RecordSourceProxy['get']>>,
): void {
  const subIssueId = subIssueRecord.getDataID()

  // Ensure titleHTML exists (use title as fallback)
  const titleHTML = subIssueRecord.getValue('titleHTML')
  if (!titleHTML) {
    const title = subIssueRecord.getValue('title')
    if (typeof title === 'string') {
      subIssueRecord.setValue(md2html(title), 'titleHTML')
    }
  }

  // Ensure assignees connection exists with the correct arguments (first: 10)
  const assigneesConnection = subIssueRecord.getLinkedRecord('assignees', {first: 10})
  if (!assigneesConnection) {
    const placeholderAssignees = store.create(`${subIssueId}:assignees:optimistic`, 'UserConnection')
    placeholderAssignees.setLinkedRecords([], 'nodes')
    placeholderAssignees.setValue(0, 'totalCount')
    subIssueRecord.setLinkedRecord(placeholderAssignees, 'assignees', {first: 10})
  }

  // Ensure issueDependenciesSummary exists (required by SubIssueStateIcon)
  const issueDependenciesSummary = subIssueRecord.getLinkedRecord('issueDependenciesSummary')
  if (!issueDependenciesSummary) {
    const placeholderDependenciesSummary = store.create(
      `${subIssueId}:issueDependenciesSummary:optimistic`,
      'IssueDependenciesSummary',
    )
    placeholderDependenciesSummary.setValue(0, 'openBlockedByCount')
    placeholderDependenciesSummary.setValue(0, 'closedBlockedByCount')
    placeholderDependenciesSummary.setValue(0, 'openBlockingCount')
    placeholderDependenciesSummary.setValue(0, 'closedBlockingCount')
    subIssueRecord.setLinkedRecord(placeholderDependenciesSummary, 'issueDependenciesSummary')
  }

  // Ensure subIssuesSummary exists (required by SubIssuesListItem_NestedSubIssues)
  const subIssuesSummary = subIssueRecord.getLinkedRecord('subIssuesSummary')
  if (!subIssuesSummary) {
    const placeholderSubIssuesSummary = store.create(`${subIssueId}:subIssuesSummary:optimistic`, 'SubIssuesSummary')
    placeholderSubIssuesSummary.setValue(0, 'total')
    placeholderSubIssuesSummary.setValue(0, 'completed')
    placeholderSubIssuesSummary.setValue(0, 'percentCompleted')
    subIssueRecord.setLinkedRecord(placeholderSubIssuesSummary, 'subIssuesSummary')
  }

  // Ensure closedByPullRequestsReferences connection exists
  const closedByPRs = subIssueRecord.getLinkedRecord('closedByPullRequestsReferences', {
    first: 0,
    includeClosedPrs: true,
  })
  if (!closedByPRs) {
    const placeholderClosedByPRs = store.create(
      `${subIssueId}:closedByPullRequestsReferences:optimistic`,
      'IssueClosedByPullRequestsReferencesConnection',
    )
    placeholderClosedByPRs.setLinkedRecords([], 'nodes')
    placeholderClosedByPRs.setValue(0, 'totalCount')
    subIssueRecord.setLinkedRecord(placeholderClosedByPRs, 'closedByPullRequestsReferences', {
      first: 0,
      includeClosedPrs: true,
    })
  }
}

function optimisticUpdateSubIssuesConnection(store: RecordSourceProxy, input: AddSubIssueInput) {
  if (!input.subIssueId) return

  const parentRecord = store.get(input.issueId)
  if (!parentRecord) return

  const subIssueRecord = store.get(input.subIssueId)
  if (!subIssueRecord) return

  // Only update if the connection already exists
  const subIssuesConnection = getSubIssuesConnection(parentRecord)
  if (!subIssuesConnection) return

  // Ensure the sub-issue has all required fields for rendering (creates placeholders if missing)
  ensureSubIssueRequiredFields(store, subIssueRecord)

  // Check if the sub-issue is already in the connection
  const currentNodes = subIssuesConnection.getLinkedRecords('nodes') || []
  const alreadyExists = currentNodes.some(node => node?.getValue('id') === input.subIssueId)

  if (!alreadyExists) {
    // Add the sub-issue to the connection
    const newNodes = [...currentNodes, subIssueRecord]
    subIssuesConnection.setLinkedRecords(newNodes, 'nodes')

    // Update totalCount on the connection
    const currentTotalCount = subIssuesConnection.getValue('totalCount')
    if (typeof currentTotalCount === 'number') {
      subIssuesConnection.setValue(currentTotalCount + 1, 'totalCount')
    } else {
      subIssuesConnection.setValue(currentNodes.length + 1, 'totalCount')
    }
  }

  // Update subIssuesSummary on the parent if it exists
  const subIssuesSummary = parentRecord.getLinkedRecord('subIssuesSummary')
  if (subIssuesSummary) {
    const currentTotal = subIssuesSummary.getValue('total')
    if (typeof currentTotal === 'number') {
      subIssuesSummary.setValue(currentTotal + 1, 'total')
    }

    // Update completed count if the sub-issue is closed
    const isClosed = subIssueRecord.getValue('closed') === true
    if (isClosed) {
      const currentCompleted = subIssuesSummary.getValue('completed')
      if (typeof currentCompleted === 'number') {
        subIssuesSummary.setValue(currentCompleted + 1, 'completed')
      }
    }
  }

  // Update subIssuesConnection totalCount (aliased field used by useSubIssuesSummary)
  const subIssuesConnectionRecord = getSubIssuesConnectionAlias(parentRecord)
  if (subIssuesConnectionRecord) {
    const connectionTotalCount = subIssuesConnectionRecord.getValue('totalCount')
    if (typeof connectionTotalCount === 'number') {
      subIssuesConnectionRecord.setValue(connectionTotalCount + 1, 'totalCount')
    }
  }
}

function addSubIssueToConnectionFromResponse(store: RecordSourceSelectorProxy, input: AddSubIssueInput) {
  const payload = store.getRootField('addSubIssue')
  if (!payload) return

  const subIssueRecord = payload.getLinkedRecord('subIssue')
  if (!subIssueRecord) return

  const parentRecord = store.get(input.issueId)
  if (!parentRecord) return

  const subIssuesConnection = getSubIssuesConnection(parentRecord)
  if (!subIssuesConnection) return

  // Check if the sub-issue is already in the connection (from optimistic update)
  const currentNodes = subIssuesConnection.getLinkedRecords('nodes') || []
  const alreadyExists = currentNodes.some(node => node?.getDataID() === subIssueRecord.getDataID())

  if (!alreadyExists) {
    // Add the sub-issue from the server response to the connection
    subIssuesConnection.setLinkedRecords([...currentNodes, subIssueRecord], 'nodes')

    // Update totalCount on the connection
    const currentTotalCount = subIssuesConnection.getValue('totalCount')
    if (typeof currentTotalCount === 'number') {
      subIssuesConnection.setValue(currentTotalCount + 1, 'totalCount')
    }
  }
}

/**
 * Maps the server-confirmed `addSubIssue.subIssue` (full data) to the secondary-view
 * `subIssues.nodes[]` shape, so the settle fold replaces the optimistic placeholders
 * with real assignees / issue type / summaries rather than leaving them blank.
 *
 * The payload selects `state` but not the row's `closed` flag, and types several fields as nullable
 * that the row requires, so the return type is what keeps this mapping and the optimistic builder
 * emitting the identical shape the pending op has to settle against.
 */
function responseSubIssueToNode(
  subIssue: NonNullable<NonNullable<addSubIssueMutation$data['addSubIssue']>['subIssue']>,
): SubIssuesListItemNode {
  const state = toRowState(subIssue.state)
  return {
    id: subIssue.id,
    closed: state === 'CLOSED',
    state,
    stateReason: toRowStateReason(subIssue.stateReason),
    issueDependenciesSummary: {blockedBy: subIssue.issueDependenciesSummary?.blockedBy ?? 0},
    assignees: {
      totalCount: subIssue.assignees?.totalCount ?? 0,
      edges: subIssue.assignees?.edges ?? [],
    },
    url: subIssue.url,
    repository: {name: subIssue.repository.name, owner: {login: subIssue.repository.owner.login}},
    databaseId: subIssue.databaseId ?? null,
    number: subIssue.number,
    title: subIssue.title,
    titleHTML: subIssue.titleHTML,
    // RELAY-CLEANUP(addSubIssueMutation): `issueType.color` is a presentational open enum whose Relay
    // variant the allowlisted SPQ row type omits; cast at this boundary rather than restating all
    // colours. Remove once addSubIssueMutation leaves Relay.
    issueType: (subIssue.issueType ?? null) as SubIssuesListItemNode['issueType'],
    subIssuesSummary: subIssue.subIssuesSummary ?? {total: 0, completed: 0},
    closedByPullRequestsReferences: {totalCount: subIssue.closedByPullRequestsReferences?.totalCount ?? 0},
  }
}

export function addSubIssueMutation({
  environment,
  input,
  onError,
  onCompleted,
  overlaySubIssue,
}: {
  environment: Environment
  input: AddSubIssueInput
  onError?: (error: Error) => void
  onCompleted?: (response: addSubIssueMutation$data) => void
  // The added issue's fields (from the picker) for the overlay node; the overlay renders from the
  // TanStack cache, not the Relay store. Only consumed when the overlay flag is on.
  overlaySubIssue?: SubIssueOptimisticNodeInput
}) {
  // Flag off = legacy path untouched: no dispatch, no operation, no mutation id. (The static query
  // always selects payload `errors` for the failure check below, but that field is unread when off.)
  const overlayEnabled = isFeatureEnabled('issue_viewer_subissues_optimistic_overlay')
  const overlayMutationId = overlayEnabled ? createConnectionMutationId() : ''

  // The operation mirrored into the TanStack overlay; `completed` delta only when the issue is closed.
  // `node.closed` is the row's own flag, so the count delta can't drift from what the list renders
  // and filters on (`SubIssuesListView` / `use-sub-issues-summary` both key off `closed`, not `state`).
  const buildAddOperation = (node: SubIssuesListItemNode): ConnectionOperation => ({
    type: 'addToConnection',
    connectionPath: SUB_ISSUES_CONNECTION_PATH,
    node,
    anchor: {at: 'end'},
    counts: [
      {path: ['subIssuesSummary', 'total'], delta: 1},
      {path: ['subIssuesConnection', 'totalCount'], delta: 1},
      ...(node.closed ? [{path: ['subIssuesSummary', 'completed'], delta: 1}] : []),
    ],
  })

  // Optimistic: the picker gives the real node id but only a subset of fields; the rest render as
  // neutral placeholders for the brief in-flight window.
  const buildOptimisticAddOperation = (): ConnectionOperation =>
    buildAddOperation(buildSubIssueOptimisticNode(overlaySubIssue ?? {id: input.subIssueId ?? '', title: ''}))

  // Settle: fold the full server node so the placeholders don't persist. It shares the real id, so
  // the still-pending optimistic op dedupes against it (no duplicate row / double count).
  const buildCompletedAddOperation = (
    subIssue: NonNullable<NonNullable<addSubIssueMutation$data['addSubIssue']>['subIssue']>,
  ): ConnectionOperation => buildAddOperation(responseSubIssueToNode(subIssue))

  // Register the optimistic overlay operation BEFORE starting the Relay subscription. commitMutation
  // subscribes synchronously, so a synchronous network Observable could fire onError/onCompleted before
  // a post-subscribe dispatch — the bridge would then treat that terminal as an orphan and the later
  // optimistic op would never settle, stranding the added row/count as pending until unmount. The engine
  // dedupes by id, so registering first is safe even when a completed terminal folds the server node first.
  if (overlayEnabled) {
    dispatchConnectionMutation({
      phase: 'optimistic',
      mutationId: overlayMutationId,
      ownerId: input.issueId,
      connectionPath: SUB_ISSUES_CONNECTION_PATH,
      operation: buildOptimisticAddOperation(),
    })
  }

  let disposable: Disposable
  try {
    disposable = commitMutation<addSubIssueMutation>(environment, {
      // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
      mutation: graphql`
        mutation addSubIssueMutation($input: AddSubIssueInput!) @raw_response_type {
          addSubIssue(input: $input) {
            errors {
              message
            }
            issue {
              id
              subIssuesSummary {
                total
                completed
              }
            }
            subIssue {
              id
              state
              stateReason
              issueDependenciesSummary {
                blockedBy
              }
              assignees(first: 10) {
                totalCount
                edges {
                  node {
                    id
                    login
                    avatarUrl
                  }
                }
              }
              url
              repository {
                name
                owner {
                  login
                }
              }
              databaseId
              number
              title
              titleHTML
              issueType {
                id
                name
                color
              }
              subIssuesSummary {
                total
                completed
              }
              closedByPullRequestsReferences(first: 0, includeClosedPrs: true) {
                totalCount
              }
            }
          }
        }
      `,
      variables: {
        input,
      },
      optimisticUpdater: store => optimisticUpdateSubIssuesConnection(store, input),
      updater: store => addSubIssueToConnectionFromResponse(store, input),
      onError: error => {
        if (overlayEnabled) dispatchConnectionMutation({phase: 'error', mutationId: overlayMutationId, error})
        onError?.(error)
      },
      onUnsubscribe: () => {
        // Relay rolls back on disposal here (not onError/onCompleted); end the bridged op too or the
        // added row/count stay optimistic until unmount. Harmless orphan error after a normal settle.
        if (overlayEnabled)
          dispatchConnectionMutation({
            phase: 'error',
            mutationId: overlayMutationId,
            error: new Error('Adding sub-issue was cancelled'),
          })
      },
      onCompleted: response => {
        if (overlayEnabled) {
          // Fail (roll back) unless the add truly succeeded. Relay signals failure via a null payload
          // or a populated payload `errors` list — both leave `subIssue` absent. Top-level `errors`
          // alongside a valid `subIssue` is not failure (e.g. only the nullable `issue` branch failed).
          const subIssue = response.addSubIssue?.subIssue
          const payloadUserErrors = response.addSubIssue?.errors?.length ?? 0
          const succeeded = response.addSubIssue != null && payloadUserErrors === 0 && subIssue != null
          dispatchConnectionMutation(
            succeeded
              ? {
                  phase: 'completed',
                  mutationId: overlayMutationId,
                  ownerId: input.issueId,
                  connectionPath: SUB_ISSUES_CONNECTION_PATH,
                  operation: buildCompletedAddOperation(subIssue),
                }
              : {
                  phase: 'error',
                  mutationId: overlayMutationId,
                  error: new Error(response.addSubIssue?.errors?.[0]?.message ?? 'Failed to add sub-issue'),
                },
          )
        }
        onCompleted?.(response)
      },
    })
  } catch (error) {
    // commitMutation threw during setup after the optimistic op was registered; settle it as an error
    // so the added row/count roll back instead of hanging pending, then rethrow for the caller.
    if (overlayEnabled)
      dispatchConnectionMutation({
        phase: 'error',
        mutationId: overlayMutationId,
        error: error instanceof Error ? error : new Error('Failed to add sub-issue'),
      })
    throw error
  }

  return disposable
}
